import { useState, useEffect } from 'react';
import { Menu, X, User, LogIn, Search, Facebook, Instagram, BarChart3, Package, Settings, HelpCircle, LogOut, ShoppingCart } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useCart } from './Cart';
import styled from 'styled-components';
import productService from '../services/productService';
import SearchSuggestions from './SearchSuggestions';

// Styled Components
const HeaderStyled = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  transition: all 0.3s ease;
  
  ${props => props.$scrolled || props.$isBoutique || props.$isCheckout || props.$isOrder || props.$isLightBgPage ? `
    background: rgba(28, 28, 30, 0.95);
    backdrop-filter: blur(0.75rem);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  ` : `
    background: transparent;
  `}
`;

const Container = styled.div`
  max-width: 80rem;
  margin: 0 auto;
  padding: 0 1rem;
  
  @media (min-width: 640px) {
    padding: 0 2rem;
  }
`;

const NavContainer = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
`;

// Palette de couleurs INZA STORE basée sur le logo
const INZA_COLORS = {
  primary: '#FF6B35', // Orange vif du logo
  secondary: '#F7931E', // Orange secondaire
  dark: '#2C3E50', // Bleu gris foncé
  light: '#ECF0F1', // Gris clair
  accent: '#E74C3C' // Rouge accent
};

const Logo = styled.a`
  font-size: 1.8rem;
  font-weight: 800;
  color: white;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: -0.02em;
  
  span {
    color: ${INZA_COLORS.primary};
    font-weight: 900;
  }
  
  &:hover {
    transform: scale(1.05);
    
    span {
      color: ${INZA_COLORS.secondary};
    }
  }
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const DesktopNav = styled.div`
  display: none;
  
  @media (min-width: 768px) {
    display: flex;
    align-items: center;
    gap: 2rem;
  }
  
  .nav-link {
    color: white;
    text-decoration: none;
    font-weight: 500;
    transition: color 0.3s ease;
    
    &:hover {
      color: ${INZA_COLORS.primary};
    }
  }
`;

const SearchBar = styled.div`
  display: none;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 9999px;
  padding: 0.5rem 1rem;
  transition: all 0.3s ease;
  
  @media (min-width: 768px) {
    display: flex;
  }
  
  &:focus-within {
    background: rgba(255, 255, 255, 0.15);
    border-color: ${INZA_COLORS.primary};
  }
  
  .search-icon {
    color: rgba(255, 255, 255, 0.6);
    margin-right: 0.5rem;
  }
  
  .search-input {
    background: transparent;
    border: none;
    color: white;
    font-size: 0.9rem;
    width: 200px;
    outline: none;
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }
    
    @media (min-width: 1024px) {
      width: 250px;
    }
  }
`;

const SocialIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    display: none; /* Masquer les social icons sur mobile */
  }
  
  @media (max-width: 480px) {
    gap: 0.5rem;
  }
  
  @media (min-width: 1024px) {
    display: flex;
  }
  
  .social-link {
    color: rgba(255, 255, 255, 0.7);
    transition: all 0.3s ease;
    
    &:hover {
      color: ${INZA_COLORS.primary};
      transform: translateY(-2px);
    }
    
    &.facebook:hover {
      color: #1877f2;
    }
    
    &.instagram:hover {
      color: #e4405f;
    }
  }
`;

const UserAvatar = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  background: linear-gradient(135deg, ${INZA_COLORS.primary}, ${INZA_COLORS.secondary});
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, ${INZA_COLORS.secondary}, ${INZA_COLORS.primary});
    transform: scale(1.05);
  }
`;

const MobileMenuButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  display: none;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  position: relative;
  z-index: 10;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: 768px) {
    display: flex;
  }
`;

const Actions = styled.div`
  display: none;
  align-items: center;
  gap: 1rem;
  position: relative;
  min-width: fit-content;
  
  @media (min-width: 768px) {
    display: flex;
  }
  
  @media (max-width: 768px) {
    display: flex;
    gap: 0.5rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.25rem;
  }
  
  .icon-btn {
    width: 2.5rem;
    height: 2.5rem;
    background: transparent;
    border: none;
    border-radius: 50%;
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  }
  
  /* Masquer uniquement le panier sur mobile */
  @media (max-width: 768px) {
    .icon-btn.cart-icon {
      display: none;
    }
  }
  
  /* Afficher uniquement la recherche sur mobile */
  @media (max-width: 768px) {
    > div:first-child {
      display: flex; /* SearchSuggestions */
    }
  }
`;

const SearchSuggestionsContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 0.5rem;
  background: rgba(28, 28, 30, 0.98);
  backdrop-filter: blur(1rem);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  padding: 0.5rem;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  
  .suggestion-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.3s ease;
    color: rgba(255, 255, 255, 0.8);
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: white;
    }
    
    .suggestion-icon {
      color: #3b82f6;
      font-size: 1.1rem;
    }
    
    .suggestion-text {
      flex: 1;
      
      .suggestion-title {
        font-weight: 500;
        margin-bottom: 0.25rem;
      }
      
      .suggestion-category {
        font-size: 0.8rem;
        color: rgba(255, 255, 255, 0.5);
      }
    }
    
    .suggestion-price {
      font-weight: 600;
      color: #10b981;
    }
  }
  
  .no-results {
    text-align: center;
    padding: 1.5rem;
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.9rem;
  }
`;

const UserMenu = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0.5rem;
  background: rgba(28, 28, 30, 0.98);
  backdrop-filter: blur(1rem);
  border-radius: 1rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  min-width: 18rem;
  max-width: 22rem;
  z-index: 1000;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .user-header {
    padding: 1.25rem;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .user-avatar {
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: bold;
    font-size: 1.125rem;
    border: 2px solid rgba(255, 255, 255, 0.2);
  }

  .user-details {
    flex: 1;
  }

  .user-name {
    font-weight: 600;
    color: white;
    margin-bottom: 0.25rem;
    font-size: 1rem;
  }

  .user-email {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.7);
    word-break: break-all;
  }

  .menu-items {
    padding: 0.5rem;
  }

  .menu-item {
    width: 100%;
    padding: 0.875rem 1rem;
    background: transparent;
    border: none;
    border-radius: 0.5rem;
    color: rgba(255, 255, 255, 0.9);
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 0.9375rem;
    text-align: left;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: white;
      transform: translateX(4px);
    }

    &.logout {
      color: #ef4444;
      
      &:hover {
        background: rgba(239, 68, 68, 0.1);
        color: #f87171;
      }
    }
  }
  
  .menu-divider {
    height: 1px;
    background: rgba(255, 255, 255, 0.1);
    margin: 0.5rem 0;
  }

  @media (max-width: 768px) {
    right: 0;
    min-width: 16rem;
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(28, 28, 30, 0.98);
  backdrop-filter: blur(1rem);
  z-index: 40;
  display: flex;
  flex-direction: column;
  padding: 2rem;
  transform: translateX(${props => props.open ? '0' : '100%'});
  transition: transform 0.3s ease;
  
  .close-btn {
    position: absolute;
    top: 2rem;
    right: 2rem;
    background: none;
    border: none;
    color: white;
    cursor: pointer;
  }
  
  .mobile-nav {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    margin-top: 4rem;
    
    .nav-link {
      color: white;
      text-decoration: none;
      font-size: 1.25rem;
      font-weight: 500;
      transition: color 0.3s ease;
      
      &:hover {
        color: #007AFF;
      }
    }
  }
  
  .mobile-actions {
    display: flex;
    gap: 1rem;
    margin-top: auto;
    
    .icon-btn {
      width: 3rem;
      height: 3rem;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      cursor: pointer;
      transition: all 0.3s ease;
      
      &:hover {
        background: rgba(255, 255, 255, 0.2);
        transform: scale(1.05);
      }
    }
  }
`;

function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [userRole, setUserRole] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { cartItems, setIsCartOpen, getCartCount } = useCart();
  
  const isBoutique = location.pathname === '/boutique';
  const isCheckout = location.pathname === '/checkout';
  const isOrder = location.pathname === '/order';
const isHomePage = location.pathname === '/';
  
  // Pages avec fond clair - texte sombre nécessaire
  const lightBgPages = ['/contact', '/faq', '/sav', '/reparations', '/about', '/legal', '/privacy'];
  const isLightBgPage = lightBgPages.includes(location.pathname);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    // Vérifier les permissions de l'utilisateur
    const checkUserPermissions = async () => {
      if (isAuthenticated && user) {
        try {
          const token = localStorage.getItem('token');
          
          const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          };

          // Ajouter le token JWT depuis localStorage (pour le développement)
          if (token) {
            // Pour le développement, utiliser X-Mock-Token pour éviter le middleware JWT
            if (token === 'mock-access-token') {
              headers['X-Mock-Token'] = token;
            } else {
              headers['Authorization'] = `Bearer ${token}`;
            }
          }
          
          const response = await fetch('https://inza-backend.sajholding.org/api/v1/user/profile/', {
            headers: headers
          });
          
          if (response.ok) {
            const data = await response.json();
            setUserRole(data.role);
          } else {
            setUserRole('customer');
          }
        } catch (error) {
          setUserRole('customer');
        }
      }
    };

    // Fermer le menu utilisateur quand on clique en dehors
    const handleClickOutside = (event) => {
      if (isUserMenuOpen && !event.target.closest('.user-actions') && !event.target.closest('UserMenu')) {
        setIsUserMenuOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    
    // Vérifier les permissions
    checkUserPermissions();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen, isAuthenticated, user]);

  // Charger les produits pour les suggestions
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const products = await productService.getProducts();
        setAllProducts(products);
      } catch (error) {
        console.error('Erreur lors du chargement des produits pour les suggestions:', error);
      }
    };

    loadProducts();
  }, []);

  const handleUserAction = (action) => {
    console.log('handleUserAction called with action:', action);
    setIsUserMenuOpen(false);
    
    switch (action) {
      case 'profile':
        navigate('/dashboard');
        break;
      case 'orders':
        navigate('/orders');
        break;
      case 'settings':
        navigate('/settings');
        break;
      case 'admin':
        navigate('/admin/products');
        break;
      case 'logout':
        handleLogout();
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  const handleLogout = async () => {
    console.log('handleLogout called');
    try {
      console.log('Calling logout function...');
      await logout();
      console.log('Logout successful, closing menu and navigating...');
      setIsUserMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      // Forcer la déconnexion même en cas d'erreur
      setIsUserMenuOpen(false);
      navigate('/');
    }
  };

  const closeUserMenu = () => {
    setIsUserMenuOpen(false);
  };

  const getUserInitial = () => {
    const testUser = user || { name: 'Test User', email: 'test@example.com' };
    if (testUser?.name) {
      return testUser.name.charAt(0).toUpperCase();
    } else if (testUser?.email) {
      return testUser.email.charAt(0).toUpperCase();
    } else if (testUser?.firstName) {
      return testUser.firstName.charAt(0).toUpperCase();
    }
    return 'U';
  };

  
  // Données des suggestions
  const searchSuggestions = [
    { id: 'iphone-15-pro-max', title: 'iPhone 15 Pro Max', category: 'iPhone', price: '918 000 FCFA', icon: '📱' },
    { id: 'iphone-15-pro', title: 'iPhone 15 Pro', category: 'iPhone', price: '787 000 FCFA', icon: '📱' },
    { id: 'iphone-15', title: 'iPhone 15', category: 'iPhone', price: '589 000 FCFA', icon: '📱' },
    { id: 'iphone-15-plus', title: 'iPhone 15 Plus', category: 'iPhone', price: '655 000 FCFA', icon: '📱' },
    { id: 'airpods-pro', title: 'AirPods Pro', category: 'Accessoires', price: '150 000 FCFA', icon: '🎧' },
    { id: 'apple-watch-9', title: 'Apple Watch Series 9', category: 'Montres', price: '450 000 FCFA', icon: '⌚' },
    { id: 'macbook-air-m2', title: 'MacBook Air M2', category: 'Mac', price: '1 350 000 FCFA', icon: '💻' },
    { id: 'ipad-pro', title: 'iPad Pro', category: 'iPad', price: '850 000 FCFA', icon: '📲' },
    { id: 'magic-keyboard', title: 'Magic Keyboard', category: 'Accessoires', price: '180 000 FCFA', icon: '⌨️' },
    { id: 'iphone-14', title: 'iPhone 14', category: 'iPhone', price: '520 000 FCFA', icon: '📱' }
  ];

  
  
  const navItems = [
    { name: 'Accueil', href: '/' },
    { name: 'Boutique', href: '/boutique' },
    { name: 'Comparaison', href: '/comparison' },
    ...(isHomePage ? [
      { name: 'Comparer', href: '#comparison' },
      { name: 'Caractéristiques', href: '#specs' },
      { name: 'Avis', href: '#reviews' },
      { name: 'Offres', href: '#offers' }
    ] : [])
  ];

  return (
    <>
      <HeaderStyled $scrolled={isScrolled} $isBoutique={isBoutique} $isCheckout={isCheckout} $isOrder={isOrder} $isLightBgPage={isLightBgPage}>
        <Container>
          <NavContainer>
            {/* Logo */}
            <Logo $isLightBgPage={isLightBgPage} href="/" onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}>
              INZA<span>STORE</span>
            </Logo>

            {/* Desktop Navigation */}
            <DesktopNav $isLightBgPage={isLightBgPage}>
              {navItems.map((item) => (
                <button
                  key={item.name}
                  className="nav-link"
                  onClick={() => item.href.startsWith('#') ? 
                    document.querySelector(item.href)?.scrollIntoView({ behavior: 'smooth' }) : 
                    navigate(item.href)
                  }
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    textDecoration: 'none',
                    fontWeight: '500',
                    transition: 'color 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => e.target.style.color = INZA_COLORS.primary}
                  onMouseLeave={(e) => e.target.style.color = 'white'}
                >
                  {item.name}
                </button>
              ))}
            </DesktopNav>

            {/* Actions */}
            <Actions $isLightBgPage={isLightBgPage} className="user-actions" style={{ position: 'relative' }}>
              {/* Cart Button */}
              <div 
                className="icon-btn cart-icon" 
                onClick={() => setIsCartOpen(true)}
                style={{ position: 'relative', marginRight: '0.5rem' }}
              >
                <ShoppingCart 
                  size={20} 
                  style={{ 
                    color: isLightBgPage ? '#1f2937' : 'white',
                    transition: 'color 0.3s ease'
                  }} 
                />
                {getCartCount() > 0 && (
                  <span 
                    className="cart-count" 
                    style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      background: '#ef4444',
                      color: 'white',
                      borderRadius: '50%',
                      width: '18px',
                      height: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '11px',
                      fontWeight: 'bold'
                    }}
                  >
                    {getCartCount()}
                  </span>
                )}
              </div>

              {/* Search Bar */}
              <SearchSuggestions placeholder="Rechercher un produit..." />

              {/* User Menu */}
              <div 
                className="user-menu"
                onMouseEnter={() => setIsUserMenuOpen(true)}
                onMouseLeave={() => setIsUserMenuOpen(false)}
              >
                {user ? (
                  <div className="user-info">
                    <button 
                      className="user-avatar-btn"
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      style={{
                        background: user?.photo || user?.profile_image || user?.avatar 
                          ? `url(${user?.photo || user?.profile_image || user?.avatar})` 
                          : 'rgba(255, 255, 255, 0.2)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: '50%',
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                      }}
                    >
                      {!(user?.photo || user?.profile_image || user?.avatar) && 
                        (user?.name ? user.name.charAt(0).toUpperCase() : user?.email?.charAt(0).toUpperCase() || 'U')
                      }
                    </button>
                    
                    {/* User Menu Dropdown - maintenant à l'intérieur de la zone de survol */}
                    {isUserMenuOpen && (
                      <UserMenu>
                        <div className="user-header">
                          <div className="user-avatar" style={{
                            background: user?.photo || user?.profile_image || user?.avatar 
                              ? `url(${user?.photo || user?.profile_image || user?.avatar})` 
                              : 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                          }}>
                            {!(user?.photo || user?.profile_image || user?.avatar) && 
                              (user?.name ? user.name.charAt(0).toUpperCase() : user?.email?.charAt(0).toUpperCase() || 'U')
                            }
                          </div>
                          <div className="user-details">
                            <div className="user-name">{user?.name || 'Utilisateur'}</div>
                            <div className="user-email">{user?.email}</div>
                          </div>
                        </div>
                        <div className="menu-items">
                          <button className="menu-item" onClick={() => handleUserAction('profile')}>
                            <User size={16} />
                            Mon Profil
                          </button>
                          <button className="menu-item" onClick={() => handleUserAction('orders')}>
                            <ShoppingCart size={16} />
                            Mes Commandes
                          </button>
                          {user?.role && ['manager', 'admin', 'super_admin'].includes(user.role) && (
                            <button className="menu-item" onClick={() => handleUserAction('admin')}>
                              <Package size={16} />
                              Administration
                            </button>
                          )}
                          <div className="menu-divider"></div>
                          <button className="menu-item logout" onClick={() => handleUserAction('logout')}>
                            <LogOut size={16} />
                            Déconnexion
                          </button>
                        </div>
                      </UserMenu>
                    )}
                  </div>
                ) : (
                  <button 
                    className="login-btn"
                    onClick={() => navigate('/login')}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.5rem 1rem',
                      color: 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <LogIn size={16} />
                    Connexion
                  </button>
                )}
              </div>
            </Actions>

            {/* Mobile Menu Button - remplace les SocialIcons sur mobile */}
            <MobileMenuButton onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </MobileMenuButton>

            {/* Social Icons - seulement sur desktop */}
            <SocialIcons>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="social-link facebook">
                <Facebook style={{ width: '1.25rem', height: '1.25rem' }} />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-link instagram">
                <Instagram style={{ width: '1.25rem', height: '1.25rem' }} />
              </a>
            </SocialIcons>
          </NavContainer>
        </Container>
      </HeaderStyled>

      {/* Mobile Menu */}
      <MobileMenu open={isMobileMenuOpen}>
        <div className="mobile-nav">
          {navItems.map((item) => (
            item.href.startsWith('/') ? (
              <button
                key={item.name}
                className="nav-link"
                onClick={() => {
                  navigate(item.href);
                  setIsMobileMenuOpen(false);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  textDecoration: 'none',
                  fontSize: '1.25rem',
                  fontWeight: '500',
                  transition: 'color 0.3s ease',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => e.target.style.color = '#007AFF'}
                onMouseLeave={(e) => e.target.style.color = 'white'}
              >
                {item.name}
              </button>
            ) : (
              <a
                key={item.name}
                href={item.href}
                className="nav-link"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.name}
              </a>
            )
          ))}
        </div>

        <div className="mobile-actions">
          {/* Mobile Cart Button */}
          <div 
            className="icon-btn" 
            onClick={() => setIsCartOpen(true)}
            style={{ position: 'relative', marginRight: '1rem' }}
          >
            <ShoppingCart style={{ width: '1.5rem', height: '1.5rem' }} />
            {getCartCount() > 0 && (
              <span 
                style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  background: '#FF6B35',
                  color: 'white',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  fontWeight: 'bold'
                }}
              >
                {getCartCount()}
              </span>
            )}
          </div>

          {user ? (
            <UserAvatar onClick={handleUserAction}>
              {getUserInitial()}
            </UserAvatar>
          ) : (
            <div className="icon-btn" onClick={handleUserAction}>
              <LogIn style={{ width: '1.5rem', height: '1.5rem' }} />
            </div>
          )}
        </div>
      </MobileMenu>
    </>
  );
};

export default Header;
